import { useTranslation } from "next-i18next";

import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import useImportChargeEstimate from "@/common/hooks/useImportChargeEstimate";
import { COUNTRY_MAP } from "@/constants/countries";
import { InfoPanel } from "@/ui/InfoPanel";
import { Flavor } from "@/ui/typography/Flavor";

type ProductDetailsInfoImportChargesProps = {
  product: ProductBySlugNonNullable;
};

/**
 * Shows a buyer whose delivery location is GB, NO or CH roughly what customs
 * will charge them on top of the price, before they get as far as the cart.
 *
 * Checkout already carries `CartUserProductImportCharges`, which warns that a
 * charge is coming without naming a figure. This is the same fact one step
 * earlier and with a number, because the decision a buyer is making on the PDP
 * is "is this worth it", and on a EUR 695 sofa into Norway the honest answer
 * moves by about EUR 174.
 *
 * Renders nothing for everyone else. The estimate is derived from the buyer's
 * delivery location, so a visitor without one or a buyer inside the EU customs
 * territory sees no extra line at all. We are not going to put a customs
 * notice on every product page in Europe to serve three countries.
 *
 * Both strings carry an English default: until they are in Lokalise the
 * default is what every locale shows, rather than the raw key.
 */
export const ProductDetailsInfoImportCharges = ({
  product,
}: ProductDetailsInfoImportChargesProps) => {
  const { t } = useTranslation();

  const estimate = useImportChargeEstimate({
    originCountry: product?.address?.country,
    // The estimate is in euros: the listing's own price, not one converted for display (WHO-5142).
    itemPrice:
      product?.buy_now_price?.unconverted?.amount ??
      product?.buy_now_price?.amount,
    // The PDP does not know which delivery option the buyer will pick, so the
    // estimate covers the goods only and the copy says "at least". Checkout
    // knows the real shipping line and restates the figure there.
    shippingPrice: 0,
    breadcrumbs: product?.categories,
  });

  if (!estimate) {
    return null;
  }

  const amount = Math.round(estimate.total);
  const countryNameKey = COUNTRY_MAP[estimate.destinationCountry]?.name;
  const country = countryNameKey
    ? t(`common:${countryNameKey}`)
    : estimate.destinationCountry;

  return (
    <InfoPanel
      className="w-full"
      icon={<IconStateInformation className="size-5 self-start" />}
      body={
        <div className="flex flex-col gap-1">
          <Flavor size="medium" className="font-bold">
            {t("pdp:pdp-import-charges-title", {
              amount,
              defaultValue: "Import charges from EUR {{amount}}",
            })}
          </Flavor>
          <Flavor size="medium">
            {t("pdp:pdp-import-charges-body", {
              amount,
              country,
              defaultValue:
                "Delivered to {{country}}, this item crosses a customs border. You pay roughly EUR {{amount}} or more in import VAT and any duty, plus the carrier's handling fee, on delivery. They are not included in the price.",
            })}
          </Flavor>
        </div>
      }
    />
  );
};

export default ProductDetailsInfoImportCharges;
